import React, { useState, useEffect } from 'react';

const EmailChannelForm = ({ channel, orgId, teamId, onSave, onCancel, apiCall }) => {
  const isTeamMode = !!teamId && !orgId;
  const [formData, setFormData] = useState({
    channelName: '',
    recipients: [''],
    ccRecipients: [''],
    bccRecipients: [''],
    isActive: true,
    isPrimary: false,
    enableBatching: false,
    batchWindowMinutes: 15,
    maxEmailsPerHour: 50,
    organizationId: orgId,
    teamId: teamId
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (channel) {
      // Load channel data
      loadChannelData();
    }
  }, [channel]);

  const loadChannelData = async () => {
    try {
      const endpoint = isTeamMode
        ? `/api/alertchannelsapi/team/${teamId}/${channel.id}`
        : `/api/alertchannelsapi/${channel.id}`;
      const data = await apiCall(endpoint);
      setFormData({
        channelName: data.channelName || '',
        recipients: data.recipients && data.recipients.length > 0 ? data.recipients : [''],
        ccRecipients: data.ccRecipients || [''],
        bccRecipients: data.bccRecipients || [''],
        isActive: data.isActive !== false,
        isPrimary: data.isPrimary === true,
        enableBatching: data.enableBatching === true,
        batchWindowMinutes: data.batchWindowMinutes || 15,
        maxEmailsPerHour: data.maxEmailsPerHour || 50,
        organizationId: orgId,
        teamId: teamId
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddRecipient = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const handleRemoveRecipient = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleRecipientChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const validateEmail = (email) => {
    const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return pattern.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate recipients
    const validRecipients = formData.recipients.filter(r => r.trim() !== '');
    if (validRecipients.length === 0) {
      setError('At least one recipient email is required');
      return;
    }

    // Validate all emails
    const allEmails = [
      ...validRecipients,
      ...formData.ccRecipients.filter(r => r.trim() !== ''),
      ...formData.bccRecipients.filter(r => r.trim() !== '')
    ];

    for (const email of allEmails) {
      if (!validateEmail(email)) {
        setError(`Invalid email address: ${email}`);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        recipients: validRecipients,
        ccRecipients: formData.ccRecipients.filter(r => r.trim() !== ''),
        bccRecipients: formData.bccRecipients.filter(r => r.trim() !== '')
      };

      if (channel) {
        // Update existing channel
        const updateEndpoint = isTeamMode
          ? `/api/alertchannelsapi/team/${teamId}/${channel.id}`
          : `/api/alertchannelsapi/${channel.id}`;
        await apiCall(updateEndpoint, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        // Create new channel
        const createEndpoint = isTeamMode
          ? '/api/alertchannelsapi/team/email'
          : '/api/alertchannelsapi/email';
        await apiCall(createEndpoint, {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>{channel ? 'Edit' : 'Create'} Email Channel</h3>
        <button
          type="button"
          onClick={onCancel}
          style={{
            border: 'none',
            background: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
        >
          ×
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          borderRadius: '6px'
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Channel Name *
          </label>
          <input
            type="text"
            value={formData.channelName}
            onChange={(e) => setFormData(prev => ({ ...prev, channelName: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Recipients * (at least one required)
          </label>
          {formData.recipients.map((recipient, index) => (
            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="email"
                value={recipient}
                onChange={(e) => handleRecipientChange('recipients', index, e.target.value)}
                placeholder="email@example.com"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              {formData.recipients.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveRecipient('recipients', index)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #dc2626',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    color: '#dc2626',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => handleAddRecipient('recipients')}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              cursor: 'pointer'
            }}
          >
            + Add Recipient
          </button>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            CC Recipients (optional)
          </label>
          {formData.ccRecipients.map((recipient, index) => (
            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="email"
                value={recipient}
                onChange={(e) => handleRecipientChange('ccRecipients', index, e.target.value)}
                placeholder="email@example.com"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <button
                type="button"
                onClick={() => handleRemoveRecipient('ccRecipients', index)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #dc2626',
                  borderRadius: '6px',
                  backgroundColor: '#ffffff',
                  color: '#dc2626',
                  cursor: 'pointer'
                }}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => handleAddRecipient('ccRecipients')}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              cursor: 'pointer'
            }}
          >
            + Add CC
          </button>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            BCC Recipients (optional)
          </label>
          {formData.bccRecipients.map((recipient, index) => (
            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="email"
                value={recipient}
                onChange={(e) => handleRecipientChange('bccRecipients', index, e.target.value)}
                placeholder="email@example.com"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <button
                type="button"
                onClick={() => handleRemoveRecipient('bccRecipients', index)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #dc2626',
                  borderRadius: '6px',
                  backgroundColor: '#ffffff',
                  color: '#dc2626',
                  cursor: 'pointer'
                }}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => handleAddRecipient('bccRecipients')}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              cursor: 'pointer'
            }}
          >
            + Add BCC
          </button>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.enableBatching}
              onChange={(e) => setFormData(prev => ({ ...prev, enableBatching: e.target.checked }))}
            />
            Enable Email Batching
          </label>
        </div>

        {formData.enableBatching && (
          <>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Batch Window (minutes)
              </label>
              <input
                type="number"
                min="5"
                max="60"
                value={formData.batchWindowMinutes}
                onChange={(e) => setFormData(prev => ({ ...prev, batchWindowMinutes: parseInt(e.target.value) || 15 }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Max Emails Per Hour
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={formData.maxEmailsPerHour}
                onChange={(e) => setFormData(prev => ({ ...prev, maxEmailsPerHour: parseInt(e.target.value) || 50 }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </>
        )}

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
            />
            Active
          </label>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.isPrimary}
              onChange={(e) => setFormData(prev => ({ ...prev, isPrimary: e.target.checked }))}
            />
            Set as Primary Channel
          </label>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              cursor: loading ? 'wait' : 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Saving...' : (channel ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </form>
  );
};

export default EmailChannelForm;

